import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Mail, Power, RefreshCw, Rocket, Send, ShieldCheck, Sparkles, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../lib/api';

/* Admin -> Email Settings (Phase 114). One place for the mail server, sender
   identity, switches, a real test email, and the queue log. Passwords are
   write-only: the server never returns them and the UI never shows them. */

const FIELDS = { host: '', port: 465, encryption: 'ssl', username: '', password: '', sender: '', sender_name: '', reply_to: '', admin_recipient: '' };

const STATE_CHIP = {
  sent: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  failed: 'bg-red-50 text-red-800 border-red-300',
  queued: 'bg-sky-50 text-sky-800 border-sky-200',
  held: 'bg-amber-50 text-amber-900 border-amber-300',
  disabled: 'bg-slate-100 text-slate-600 border-slate-200',
  simulated: 'bg-violet-50 text-violet-800 border-violet-300',
};

/* Post-deploy smoke check: read-only probe of a site (Production or this
   environment) confirming the Email Settings, share-link and pin tooling is
   live. Nothing is written on the target, no email is sent, no login happens. */
const GROUP_LABELS = { core: 'Core API', email: 'Email Settings tooling', share: 'Share links', pins: 'Pin tools' };

const DeployCheckCard = () => {
  const [target, setTarget] = useState('production');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = async () => {
    setRunning(true); setError(''); setResult(null);
    try {
      const r = await api.get('/admin/deploy-check', { params: { target }, timeout: 120000 });
      setResult(r.data);
      (r.data.failed === 0 ? toast.success : toast.warning)(r.data.failed === 0 ? `All ${r.data.total} checks passed on ${r.data.target_label}` : `${r.data.failed} of ${r.data.total} checks failed on ${r.data.target_label}`);
    } catch (e) { setError(e.response?.data?.detail || 'The check could not run'); toast.error('Deploy check failed to run'); } finally { setRunning(false); }
  };
  return (
    <Card data-testid="deploy-check-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Rocket className="h-4 w-4 text-[#C9A84C]" /> Post-deploy smoke check</p>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl">Run this after each deployment. It confirms, read-only, that the Email Settings tooling, permanent share links and pin tools are live on the chosen site. It writes nothing, sends no email and does not log in. A pass does not prove SMTP delivery or that individual pins are correctly placed.</p>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={target} onValueChange={setTarget}>
              <TabsList className="h-9">
                <TabsTrigger value="production" className="text-xs" data-testid="deploy-check-target-production">Production (stevelesperance.com)</TabsTrigger>
                <TabsTrigger value="here" className="text-xs" data-testid="deploy-check-target-here">This environment</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button onClick={run} disabled={running} className="h-9 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="deploy-check-run">{running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />} Run check</Button>
          </div>
        </div>
        {error && <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="deploy-check-error">{error}</p>}
        {result && (
          <div className="space-y-3" data-testid="deploy-check-result">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded-full border px-2.5 py-1 font-bold ${result.failed === 0 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-red-50 text-red-800 border-red-300'}`} data-testid="deploy-check-summary">{result.passed}/{result.total} passed on {result.target_label}</span>
              {Object.entries(result.groups || {}).map(([g, c]) => (
                <span key={g} className={`rounded-full border px-2.5 py-1 font-bold ${c.passed === c.total ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-900 border-amber-300'}`} data-testid={`deploy-check-group-${g}`}>{GROUP_LABELS[g] || g}: {c.passed}/{c.total}</span>
              ))}
            </div>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
              {result.rows.map((r, i) => (
                <li key={`${r.group}-${r.name}`} className="flex items-start gap-2 px-3 py-2 text-xs" data-testid={`deploy-check-row-${i}`}>
                  {r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-700 shrink-0 mt-0.5" />}
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-[#1a2744]">{r.name}</span>
                    <span className="block text-slate-500 break-words">{r.detail}{!r.ok && r.expect ? ` (expected ${r.expect})` : ''}</span>
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0">{GROUP_LABELS[r.group] || r.group}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-500">{result.note}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminEmailSettings() {
  const [view, setView] = useState(null);
  const [form, setForm] = useState(FIELDS);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [log, setLog] = useState({ rows: [], counts: {} });
  const [logState, setLogState] = useState('all');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    const r = await api.get('/admin/email-settings');
    setView(r.data);
    setForm((f) => ({ ...f, host: r.data.host || '', port: r.data.port || 465, encryption: r.data.encryption || 'ssl', username: r.data.username || '', sender: r.data.sender || '', sender_name: r.data.sender_name || '', reply_to: r.data.reply_to || '', admin_recipient: r.data.admin_recipient || '', password: '' }));
    if (!testTo) setTestTo(r.data.admin_recipient || '');
  }, [testTo]);
  const loadLog = useCallback(async () => {
    const r = await api.get('/admin/issue-emails', { params: { state: logState, limit: 100 } });
    setLog(r.data);
  }, [logState]);
  useEffect(() => { load().catch(() => toast.error('Could not load email settings')); }, [load]);
  useEffect(() => { loadLog().catch(() => {}); }, [loadLog]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, port: Number(form.port) || 465 };
      if (!body.password) delete body.password;   // blank keeps the stored password
      await api.put('/admin/email-settings', body);
      toast.success('Email settings saved');
      setForm((f) => ({ ...f, password: '' }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  const verify = async () => {
    setVerifying(true);
    try {
      const r = await api.post('/admin/email-settings/verify');
      if (r.data.ok) toast.success('Mail server connection and login OK'); else toast.error(r.data.error || 'Verification failed');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); } finally { setVerifying(false); }
  };
  const sendTest = async () => {
    if (!testTo) { toast.error('Enter a test recipient'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/admin/email-settings/test', { to: testTo });
      setTestResult(r.data);
      (r.data.ok ? toast.success : toast.error)(r.data.ok ? 'Test email accepted by the mail server' : 'Test email failed');
      await load();
      await loadLog();
    } catch (e) { const d = e.response?.data?.detail; setTestResult({ ok: false, error: d }); toast.error(d || 'Test failed'); } finally { setTesting(false); }
  };
  const toggleActive = async (on) => {
    try {
      await api.post(`/admin/email-settings/${on ? 'activate' : 'deactivate'}`);
      toast.success(on ? 'Resident emails switched ON' : 'Resident emails switched OFF');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change the switch'); }
  };
  const toggleSimulation = async (on) => {
    try {
      const r = await api.post('/admin/email-settings/simulation', { enabled: on });
      setView(r.data);
      (on ? toast.warning : toast.success)(on ? 'Simulation mode ON: nothing will be sent to anyone' : 'Simulation mode OFF: real sending is possible again');
      await loadLog();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change simulation mode'); }
  };
  const toggleWeekly = async (on) => {
    try { await api.put('/admin/weekly-summary', { enabled: on }); toast.success(on ? 'Weekly admin digest ON' : 'Weekly admin digest OFF'); await load(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not change the digest switch'); }
  };
  const retry = async (row, force = false) => {
    setBusyId(row.id);
    try {
      await api.post(`/admin/issue-emails/${row.id}/retry`, { force });
      toast.success('Retry attempted; see the new state');
      await loadLog();
    } catch (e) {
      const d = e.response?.data?.detail || 'Retry failed';
      if (e.response?.status === 409 && !force && window.confirm(`${d}\n\nRetry anyway?`)) return retry(row, true);
      toast.error(d);
    } finally { setBusyId(''); }
  };

  if (!view) return <div className="flex items-center gap-2 text-slate-500 p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading email settings...</div>;

  return (
    <div className="space-y-6" data-testid="admin-email-settings-page">
      <div>
        <h1 className="text-2xl font-black text-[#1a2744]">Email Settings</h1>
        <p className="text-sm text-slate-600 mt-1">Mail server, sender identity, switches and the email log for resident receipts, status updates and the weekly admin digest.</p>
      </div>

      {view.simulation && (
        <div className="rounded-xl border border-violet-300 bg-violet-50 px-4 py-3 text-sm text-violet-900 flex items-start gap-2" data-testid="email-simulation-banner">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span><strong>Simulation mode is ON.</strong> The full queue and send workflow runs, but no connection is made to any mail server and nobody receives anything. Messages are logged as "Simulated (no real email)". Turn it off below before real sending.</span>
        </div>
      )}

      {/* Setup checklist */}
      <Card data-testid="email-setup-steps">
        <CardContent className="p-5">
          <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#C9A84C]" /> Setup</p>
          <ol className="mt-3 space-y-2">
            {view.setup_steps.map((s, i) => (
              <li key={s.key} className="flex items-start gap-2.5 text-sm" data-testid={`email-setup-step-${s.key}`}>
                {s.done ? <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0" /> : <span className="h-5 w-5 rounded-full border-2 border-slate-300 text-[11px] font-bold text-slate-500 inline-flex items-center justify-center shrink-0">{i + 1}</span>}
                <span className={s.done ? 'text-slate-500 line-through' : 'text-slate-800'}>{s.label}</span>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-2 mt-4 text-xs">
            <span className={`rounded-full border px-2.5 py-1 font-bold ${view.real_configured ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-amber-50 text-amber-900 border-amber-300'}`} data-testid="email-configured-chip">{view.real_configured ? 'Mail settings complete' : 'Mail settings incomplete'}</span>
            {view.simulation && <span className="rounded-full border px-2.5 py-1 font-bold bg-violet-50 text-violet-800 border-violet-300" data-testid="email-simulation-chip">Simulation ON (no real sending)</span>}
            <span className={`rounded-full border px-2.5 py-1 font-bold ${view.active ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid="email-active-chip">Resident emails {view.active ? 'ON' : 'OFF'}</span>
            <span className={`rounded-full border px-2.5 py-1 font-bold ${view.weekly_summary_enabled ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid="email-weekly-chip">Weekly digest {view.weekly_summary_enabled ? 'ON' : 'OFF'}</span>
            {view.last_verify_error && <span className="rounded-full border px-2.5 py-1 font-bold bg-red-50 text-red-800 border-red-300" data-testid="email-verify-error">Last check failed: {view.last_verify_error.slice(0, 80)}</span>}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Server + identity */}
        <Card data-testid="email-server-card">
          <CardContent className="p-5 space-y-4">
            <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Mail className="h-4 w-4" /> Mail server and sender</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="es-host">SMTP host</Label>
                <Input id="es-host" value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="mail.example.com" className="mt-1 h-11" data-testid="email-host-input" />
              </div>
              <div>
                <Label htmlFor="es-port">Port</Label>
                <Input id="es-port" type="number" value={form.port} onChange={(e) => set('port', e.target.value)} className="mt-1 h-11" data-testid="email-port-input" />
              </div>
            </div>
            <div>
              <Label>Encryption</Label>
              <Select value={form.encryption} onValueChange={(v) => set('encryption', v)}>
                <SelectTrigger className="mt-1 h-11" data-testid="email-encryption-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ssl">SSL/TLS (usually port 465)</SelectItem>
                  <SelectItem value="starttls">STARTTLS (usually port 587)</SelectItem>
                  <SelectItem value="none">None (not recommended)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="es-user">Username</Label>
                <Input id="es-user" value={form.username} onChange={(e) => set('username', e.target.value)} autoComplete="off" className="mt-1 h-11" data-testid="email-username-input" />
              </div>
              <div>
                <Label htmlFor="es-pass">Password {view.has_password && <span className="text-slate-400 font-normal">(stored: {view.password_masked}; leave blank to keep)</span>}</Label>
                <Input id="es-pass" type="password" value={form.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" placeholder={view.has_password ? '••••••••' : 'Mailbox password'} className="mt-1 h-11" data-testid="email-password-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="es-sender-name">Sender name</Label>
                <Input id="es-sender-name" value={form.sender_name} onChange={(e) => set('sender_name', e.target.value)} className="mt-1 h-11" data-testid="email-sender-name-input" />
              </div>
              <div>
                <Label htmlFor="es-sender">Sender address</Label>
                <Input id="es-sender" value={form.sender} onChange={(e) => set('sender', e.target.value)} placeholder="info@stevelesperance.com" className="mt-1 h-11" data-testid="email-sender-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="es-reply">Reply-to address <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input id="es-reply" value={form.reply_to} onChange={(e) => set('reply_to', e.target.value)} className="mt-1 h-11" data-testid="email-reply-to-input" />
              </div>
              <div>
                <Label htmlFor="es-admin">Admin notification recipient</Label>
                <Input id="es-admin" value={form.admin_recipient} onChange={(e) => set('admin_recipient', e.target.value)} className="mt-1 h-11" data-testid="email-admin-recipient-input" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={save} disabled={saving} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="email-save-button">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save settings</Button>
              <Button onClick={verify} disabled={verifying || !view.configured} variant="outline" className="h-11 font-bold btn-press" data-testid="email-verify-button">{verifying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />} Check connection</Button>
            </div>
            <p className="text-[11px] text-slate-500">The password is stored server-side, never shown again, never logged and never returned by the API.</p>
          </CardContent>
        </Card>

        {/* Test + switches */}
        <div className="space-y-6">
          <Card data-testid="email-test-card">
            <CardContent className="p-5 space-y-3">
              <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Send className="h-4 w-4" /> Send Test Email</p>
              <p className="text-xs text-slate-600">{view.simulation ? 'Simulation mode is on: this runs the workflow and logs a Simulated record. No email is sent to anyone.' : 'Sends a real message so you can confirm it lands in an inbox. "Accepted by the mail server" is not proof of delivery; check the inbox. Result is recorded in the log below.'}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="h-11 sm:flex-1 shrink-0" data-testid="email-test-recipient-input" />
                <Button onClick={sendTest} disabled={testing || !view.configured} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="email-test-send-button">{testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send Test Email</Button>
              </div>
              {!view.configured && <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="email-test-needs-config">Save host, username and password first.</p>}
              {testResult && (
                <div className={`text-sm rounded-lg border px-3 py-2 ${testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`} data-testid="email-test-result">
                  {testResult.ok ? <p><strong>{testResult.simulated ? 'Simulated only.' : 'Accepted by the mail server.'}</strong> {testResult.message}</p> : <p><strong>Failed.</strong> {testResult.explanation || testResult.error}</p>}
                </div>
              )}
              {view.last_test_at && <p className="text-[11px] text-slate-500">Last test: {view.last_test_at.slice(0, 16).replace('T', ' ')} to {view.last_test_to}: {view.last_test_result}</p>}
            </CardContent>
          </Card>

          <Card data-testid="email-switches-card">
            <CardContent className="p-5 space-y-4">
              <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Power className="h-4 w-4" /> Switches</p>
              <label className="flex items-start justify-between gap-3 cursor-pointer">
                <span>
                  <span className="block text-sm font-bold text-[#1a2744]">Resident emails</span>
                  <span className="block text-xs text-slate-600 mt-0.5">Receipts with tracking number and complaint copy, status updates, and warm resolution/closing notices. Off means new messages wait in the queue.</span>
                </span>
                <Switch checked={view.active} onCheckedChange={toggleActive} disabled={!view.configured} data-testid="email-active-switch" />
              </label>
              <label className="flex items-start justify-between gap-3 cursor-pointer">
                <span>
                  <span className="block text-sm font-bold text-[#1a2744]">Weekly admin digest</span>
                  <span className="block text-xs text-slate-600 mt-0.5">Monday 8:00 AM Toronto time to the admin recipient. Independent of the resident switch.</span>
                </span>
                <Switch checked={view.weekly_summary_enabled} onCheckedChange={toggleWeekly} data-testid="email-weekly-switch" />
              </label>
              {!view.configured && <p className="text-xs text-slate-500 inline-flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" /> Resident emails can be switched on after the mail settings pass a connection check.</p>}
              <label className="flex items-start justify-between gap-3 cursor-pointer border-t border-slate-100 pt-4">
                <span>
                  <span className="block text-sm font-bold text-violet-900">Simulation mode (no real sending)</span>
                  <span className="block text-xs text-slate-600 mt-0.5">For testing the workflow safely. While on, every send is logged as Simulated and nothing reaches any mailbox. Real delivery tests need this OFF.</span>
                </span>
                <Switch checked={!!view.simulation} onCheckedChange={toggleSimulation} data-testid="email-simulation-switch" />
              </label>
            </CardContent>
          </Card>
        </div>
      </div>

      <DeployCheckCard />

      {/* Log */}
      <Card data-testid="email-log-card">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-serif font-bold text-[#1a2744]">Email log</p>
              <p className="text-xs text-slate-600 mt-0.5">"Accepted by mail server" means the server took the message; it is not proof it reached the inbox. Only the recipient can confirm delivery.</p>
            </div>
            <div className="flex items-center gap-2">
              <Tabs value={logState} onValueChange={setLogState}>
                <TabsList className="h-9">
                  {['all', 'queued', 'held', 'sent', 'failed', 'simulated'].map((s) => (
                    <TabsTrigger key={s} value={s} className="text-xs capitalize" data-testid={`email-log-tab-${s}`}>{s === 'sent' ? 'Accepted' : s}{s !== 'all' && log.counts?.[s] != null ? ` (${log.counts[s]})` : ''}</TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <Button size="sm" variant="outline" onClick={loadLog} className="h-9 font-bold" data-testid="email-log-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          {log.rows.length === 0 ? (
            <p className="text-sm text-slate-500 mt-4" data-testid="email-log-empty">No email records{logState !== 'all' ? ` in state "${logState}"` : ''}.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs" data-testid="email-log-table">
                <thead><tr className="text-left text-slate-500"><th className="py-1.5 pr-3">When</th><th className="py-1.5 pr-3">State</th><th className="py-1.5 pr-3">Kind</th><th className="py-1.5 pr-3">To</th><th className="py-1.5 pr-3">Report</th><th className="py-1.5 pr-3">Explanation</th><th className="py-1.5">Action</th></tr></thead>
                <tbody>
                  {log.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`email-log-row-${r.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{(r.created_at || '').slice(0, 16).replace('T', ' ')}<div className="text-[10px] text-slate-400">{r.attempts ? `${r.attempts} attempt${r.attempts === 1 ? '' : 's'}` : 'not attempted'}</div></td>
                      <td className="py-2 pr-3"><span className={`rounded-full border px-2 py-0.5 font-bold whitespace-nowrap ${STATE_CHIP[r.state] || STATE_CHIP.disabled}`} data-testid="email-log-state">{r.state_label}</span></td>
                      <td className="py-2 pr-3 capitalize font-semibold text-[#1a2744]">{r.kind}{r.resend_of ? ' (resend)' : ''}</td>
                      <td className="py-2 pr-3 text-slate-700 break-all max-w-[180px]">{r.to_email}</td>
                      <td className="py-2 pr-3 font-mono text-slate-700 whitespace-nowrap">{r.tracking_number || '-'}</td>
                      <td className="py-2 pr-3 text-slate-600 max-w-[280px]">{r.error ? <span className="text-red-800">{r.error_explanation || r.error}</span> : r.held_reason || r.state_meaning}</td>
                      <td className="py-2 whitespace-nowrap">
                        {r.can_retry && r.kind !== 'test' && r.kind !== 'share' && (
                          <Button size="sm" variant="outline" onClick={() => retry(r)} disabled={busyId === r.id || !view.configured} className="h-8 text-[11px] font-bold" data-testid={`email-log-retry-${r.id}`}>{busyId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retry / send now'}</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
